// /Users/aminajackson/Git-projects/pro-maximizer/frontend/src/NewProject.tsx

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Chip,
  LinearProgress,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Checkbox,
  Avatar,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  FormGroup,
  OutlinedInput,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  PlayArrow as PlayArrowIcon,
  Settings as SettingsIcon,
  Upload as UploadIcon,
  Timeline as TimelineIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useAppContext } from './AppContext';
import ThemeAwareMarkdown from './ThemeAwareMarkdown';

import { API_BASE_URL } from './api';

const FEATURES = [
    { key: 'readiness', label: 'Readiness Assessment', icon: <CheckCircleIcon />, color: 'success.main' },
    { key: 'sow', label: 'Statement of Work Draft', icon: <InfoIcon />, color: 'info.main' },
    { key: 'optimization', label: 'Optimization & Scaling', icon: <SettingsIcon />, color: 'warning.main' },
    { key: 'milestones', label: 'Milestone Generation', icon: <TimelineIcon />, color: 'secondary.main' },
    { key: 'quality', label: 'Product Quality', icon: <CheckCircleIcon />, color: 'success.dark' },
    { key: 'roles', label: 'Role & Skill Recommendations', icon: <GroupIcon />, color: 'grey.600' },
    { key: 'scenarios', label: 'Scenario Models', icon: <AnalyticsIcon />, color: 'error.main' },
];

const MODES = [
    { key: 'Total Automation', label: 'Total Automation', description: 'Guide the AI with a description and feature selection.', color: 'primary.main', icon: <PlayArrowIcon /> },
    { key: 'Smart Hybrid', label: 'Smart Hybrid', description: 'Add an optimization goal to your AI-assisted plan.', color: 'success.main', icon: <AnalyticsIcon /> },
    { key: 'Full Control', label: 'Full Control', description: 'Provide detailed requirements for each feature.', color: 'warning.main', icon: <SettingsIcon /> },
];

// FIX: Removed the 'setSelectedSidebarItem' prop as it's no longer needed.
interface NewProjectProps {
    onProjectCreate: (formData: any) => void;
    onGoBack: () => void;
    planMode: string;
    setPlanMode: React.Dispatch<React.SetStateAction<string>>;
    totalAutomationResult: any;
    setTotalAutomationResult: React.Dispatch<React.SetStateAction<any>>;
    generatedProjectName: string;
    setGeneratedProjectName: React.Dispatch<React.SetStateAction<string>>;
}

interface Board {
  id: number;
  name: string;
  project_id: number | null;
}

const NewProject: React.FC<NewProjectProps> = ({
  onProjectCreate,
  onGoBack,
  planMode,
  setPlanMode,
  totalAutomationResult,
  setTotalAutomationResult,
  generatedProjectName,
  setGeneratedProjectName,
  // FIX: 'setSelectedSidebarItem' is removed from destructuring.
}) => {
    const { teamMembers } = useAppContext();
    const [projectDescriptionInput, setProjectDescriptionInput] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
    const [generationError, setGenerationError] = useState<{ message: string; details_needed?: string[] } | null>(null);

    const [selectedFeatures, setSelectedFeatures] = useState<string[]>(FEATURES.map(f => f.key));
    const [fullControlInputs, setFullControlInputs] = useState<{ [key: string]: string }>({});
    const [optimizationStrategy, setOptimizationStrategy] = useState('balance');

    const [projectId, setProjectId] = useState('');
    const [manager, setManager] = useState('');
    const [team, setTeam] = useState<string[]>([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [boardOption, setBoardOption] = useState('new');
    const [newBoardType, setNewBoardType] = useState('kanban');
    const [newBoardName, setNewBoardName] = useState('');

    // FIX: Removed 'createdProject' state as the parent now handles the post-creation flow.
    // const [createdProject, setCreatedProject] = useState<any>(null);

    const [existingBoards, setExistingBoards] = useState<Board[]>([]);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [finalizationError, setFinalizationError] = useState<string | null>(null);

    useEffect(() => {
        if (generatedProjectName) {
            const suggestedId = generatedProjectName.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').slice(0, 50);
            setProjectId(suggestedId);
            const capitalizedBoardType = newBoardType.charAt(0).toUpperCase() + newBoardType.slice(1);
            setNewBoardName(`${generatedProjectName} ${capitalizedBoardType} Board`);
        }
    }, [generatedProjectName, newBoardType]);

    useEffect(() => {
        const fetchBoards = async () => {
            if (totalAutomationResult) {
                try {
                    const response = await fetch(`${API_BASE_URL}/api/boards`);
                    if (!response.ok) throw new Error('Failed to fetch boards');
                    const allBoards: Board[] = await response.json();
                    const unassignedBoards = allBoards.filter(b => b.project_id === null);
                    setExistingBoards(unassignedBoards);
                } catch (error) {
                    console.error("Error fetching boards:", error);
                    setFinalizationError('Could not load existing boards.');
                }
            }
        };
        fetchBoards();
    }, [totalAutomationResult]);


    const handleFeatureToggle = (featureKey: string) => {
        setSelectedFeatures(prev =>
            prev.includes(featureKey) ? prev.filter(k => k !== featureKey) : [...prev, featureKey]
        );
    };

    const handleFullControlInputChange = (featureKey: string, value: string) => {
        setFullControlInputs(prev => ({ ...prev, [featureKey]: value }));
    };

    const handleGeneratePlan = useCallback(async () => {
        setIsGeneratingPlan(true);
        setGenerationError(null);
        setTotalAutomationResult(null);

        const formData = new FormData();
        files.forEach(file => formData.append('files', file));

        let endpoint = '/project/generate-total-automation';

        if (planMode === 'Total Automation' || planMode === 'Smart Hybrid') {
            if (projectDescriptionInput) formData.append('project_description', projectDescriptionInput);
            if (selectedFeatures.length === 0) {
                setGenerationError({ message: 'Please select at least one feature to generate.' });
                setIsGeneratingPlan(false);
                return;
            }
            formData.append('selected_features', JSON.stringify(selectedFeatures));
            if (planMode === 'Smart Hybrid') {
                formData.append('optimization_strategy', optimizationStrategy);
            }
        } else if (planMode === 'Full Control') {
            setGenerationError({ message: 'Full Control mode is not yet implemented.' });
            setIsGeneratingPlan(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}${endpoint}`, { method: 'POST', body: formData });
            const data = await response.json();
            if (!response.ok) throw data.detail || { message: 'An unknown error occurred.' };
            setTotalAutomationResult(data);
            setGeneratedProjectName(data.projectName);
        } catch (error: any) {
            setGenerationError(error);
        } finally {
            setIsGeneratingPlan(false);
        }
    }, [planMode, projectDescriptionInput, files, selectedFeatures, optimizationStrategy, setTotalAutomationResult, setGeneratedProjectName]);

    // FIX: Simplified the creation handler. It now only calls the onProjectCreate callback.
    const handleFinalizeAndCreate = async () => {
        setIsFinalizing(true);
        setFinalizationError(null);

        if (boardOption === 'new' && !newBoardName) {
            setFinalizationError('Please provide a name for the new board.');
            setIsFinalizing(false);
            return;
        }

        const payload = {
            id: projectId,
            name: generatedProjectName,
            planData: totalAutomationResult,
            manager,
            team,
            startDate,
            endDate,
            boardOption: boardOption,
            newBoardType: newBoardType,
            newBoardName: newBoardName,
        };

        try {
            const response = await fetch(`${API_BASE_URL}/projects/finalize`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'An unknown error occurred during finalization.');
            }

            const newProject = await response.json();
            // This callback notifies the parent, which handles the view change.
            onProjectCreate(newProject);
        } catch (error: any) {
            setFinalizationError(error.message);
        } finally {
            setIsFinalizing(false);
        }
    };

    const renderModeUI = () => {
        switch (planMode) {
            case 'Total Automation':
            case 'Smart Hybrid':
                return (
                    <>
                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            label="Project Description"
                            placeholder="Describe the goals, scope, key features, and target audience..."
                            value={projectDescriptionInput}
                            onChange={(e) => setProjectDescriptionInput(e.target.value)}
                            variant="outlined"
                            sx={{ mb: 2 }}
                        />
                        {planMode === 'Smart Hybrid' && (
                            <FormControl fullWidth sx={{ my: 2 }}>
                                <InputLabel>Optimization Goal</InputLabel>
                                <Select
                                    value={optimizationStrategy}
                                    label="Optimization Goal"
                                    onChange={(e) => setOptimizationStrategy(e.target.value)}
                                >
                                    <MenuItem value="balance">Balance</MenuItem>
                                    <MenuItem value="time">Time Optimized</MenuItem>
                                    <MenuItem value="budget">Budget Optimized</MenuItem>
                                    <MenuItem value="product">Product Optimized</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom>Select Features to Include</Typography>
                        <FormGroup>
                            {FEATURES.map(feature => (
                                <FormControlLabel
                                    key={feature.key}
                                    control={<Checkbox checked={selectedFeatures.includes(feature.key)} onChange={() => handleFeatureToggle(feature.key)} />}
                                    label={feature.label}
                                />
                            ))}
                        </FormGroup>
                    </>
                );
            case 'Full Control':
                 return <Alert severity="info">Full Control mode is coming soon.</Alert>;
            default:
                return null;
        }
    };

    if (!planMode) {
        return (
            <Box sx={{ py: 4 }}>
                <Typography variant="h5" align="center" gutterBottom>Choose Your Planning Mode</Typography>
                <Grid container spacing={3} justifyContent="center" sx={{mt: 2}}>
                    {MODES.map((mode) => (
                        <Grid item xs={12} sm={6} md={4} key={mode.key}>
                            <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }} onClick={() => setPlanMode(mode.key)}>
                                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2, bgcolor: mode.color, color: 'white' }}>{mode.icon}</Avatar>
                                    <Typography variant="h6" gutterBottom>{mode.label}</Typography>
                                    <Typography variant="body2" color="text.secondary">{mode.description}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    return (
        <Box sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">{planMode}</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => { setPlanMode(''); setTotalAutomationResult(null); }}>Change Mode</Button>
            </Box>

            {!totalAutomationResult ? (
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Define Project Scope</Typography>
                        {renderModeUI()}
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" gutterBottom>Upload Supporting Documents (Optional)</Typography>
                        <Button variant="outlined" component="label" startIcon={<UploadIcon />} fullWidth sx={{ mt: 2, mb: 1 }}>
                            Upload Files <input type="file" accept=".pdf,.docx,.xlsx,.csv,.txt" hidden multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
                            Allowed formats: .pdf, .docx, .xlsx, .csv, .txt
                        </Typography>
                        {files.length > 0 && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>{files.map(file => <Chip key={file.name} label={file.name} onDelete={() => setFiles(files.filter(f => f.name !== file.name))} />)}</Box>}
                        {isGeneratingPlan && <LinearProgress sx={{ my: 2 }} />}
                        {generationError && <Alert severity="error" sx={{ my: 2 }}>{generationError.message}</Alert>}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button variant="contained" size="large" onClick={handleGeneratePlan} disabled={isGeneratingPlan} startIcon={<PlayArrowIcon />}>
                                {isGeneratingPlan ? 'Generating Plan...' : 'Generate Plan'}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            ) : (
                // FIX: The success screen logic is removed. The component now only shows the finalization form.
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h5">Finalize Project</Typography>
                        <Button onClick={() => setTotalAutomationResult(null)}>Go Back & Edit</Button>
                    </Box>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={7}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>Project Details</Typography>
                                <Stack spacing={2}>
                                    <TextField fullWidth label="Project ID" value={projectId} onChange={(e) => setProjectId(e.target.value)} helperText="Unique identifier for the project (auto-generated)." />
                                    <TextField fullWidth label="Project Name" value={generatedProjectName} onChange={(e) => setGeneratedProjectName(e.target.value)} />
                                    <FormControl fullWidth><InputLabel>Project Manager</InputLabel><Select value={manager} label="Project Manager" onChange={(e) => setManager(e.target.value)}>{teamMembers.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}</Select></FormControl>
                                    <FormControl fullWidth><InputLabel>Team Members</InputLabel><Select multiple value={team} onChange={(e) => setTeam(e.target.value as string[])} input={<OutlinedInput label="Team Members" />} renderValue={(selected) => <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{selected.map(value => <Chip key={value} label={value} />)}</Box>}>{teamMembers.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}</Select></FormControl>
                                    <Stack direction="row" spacing={2}><TextField fullWidth type="date" label="Start Date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} /><TextField fullWidth type="date" label="End Date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} /></Stack>
                                </Stack>
                            </Paper>
                            <Paper sx={{ p: 3, mt: 3 }}>
                                <Typography variant="h6" gutterBottom>Board Setup</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Automatically create and populate a board, or link to an existing one.</Typography>
                                <FormControl fullWidth>
                                    <InputLabel>Board Option</InputLabel>
                                    <Select value={boardOption} label="Board Option" onChange={(e) => setBoardOption(e.target.value)}>
                                        <MenuItem value="new">Create a new board</MenuItem>
                                        <MenuItem value="none">Do not create a board</MenuItem>
                                        {existingBoards.length > 0 && <Divider />}
                                        {existingBoards.length > 0 && <MenuItem disabled><em>Link to existing unassigned board:</em></MenuItem>}
                                        {existingBoards.map((board: Board) => (
                                            <MenuItem key={board.id} value={String(board.id)}>{board.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {boardOption === 'new' && (
                                    <Box sx={{ mt: 2 }}>
                                        <TextField
                                            fullWidth
                                            label="New Board Name"
                                            value={newBoardName}
                                            onChange={(e) => setNewBoardName(e.target.value)}
                                            placeholder="e.g., My Project Kanban"
                                            required
                                        />
                                        <FormControl sx={{ ml: 2, mt: 2 }}>
                                            <Select value={newBoardType} onChange={(e) => setNewBoardType(e.target.value)}>
                                                {[<MenuItem key="kanban" value="kanban">Kanban</MenuItem>, <MenuItem key="agile" value="agile">Agile/Scrum</MenuItem>]}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                )}
                            </Paper>
                            <Box sx={{ mt: 3, textAlign: 'right' }}>
                                {finalizationError && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{finalizationError}</Alert>}
                                <Button variant="contained" size="large" onClick={handleFinalizeAndCreate} disabled={isFinalizing}>
                                    {isFinalizing ? <CircularProgress size={24} color="inherit" /> : 'Create Project'}
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Typography variant="h6" gutterBottom>Generated Plan Summary</Typography>
                            <Stack spacing={1} sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
                                {totalAutomationResult.projectSummary && (
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle1">Project Summary</Typography></AccordionSummary>
                                        <AccordionDetails><ThemeAwareMarkdown>{totalAutomationResult.projectSummary}</ThemeAwareMarkdown></AccordionDetails>
                                    </Accordion>
                                )}
                                {FEATURES.filter(f => f.key !== 'projectSummary').map(feature => (
                                    totalAutomationResult[feature.key] && (
                                        <Accordion key={feature.key}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>{feature.label}</Typography></AccordionSummary>
                                            <AccordionDetails>
                                                <ThemeAwareMarkdown>{typeof totalAutomationResult[feature.key] === 'string' ? totalAutomationResult[feature.key] : `\`\`\`json\n${JSON.stringify(totalAutomationResult[feature.key], null, 2)}\n\`\`\``}</ThemeAwareMarkdown>
                                            </AccordionDetails>
                                        </Accordion>
                                    )
                                ))}
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Box>
    );
};

export default NewProject;